/**
 * Real-time pitch detection — YIN (de Cheveigné & Kawahara 2002), in-house.
 *
 * PORTED VERBATIM from the NSM Sight Singing Studio (src/lib/pitch-detector.ts),
 * which in turn came from NSM Melodic Curves. Keep the two in sync by copying,
 * not by rewriting: this file carries hard-won fixes (low male voices returning
 * ~19 kHz garbage, the "live stream sending digital silence" Zoom symptom,
 * interval-driven analysis so a throttled background tab keeps listening).
 * Ported from NSM Melodic Curves (battle-tested against low male voices where
 * naive implementations return ~19 kHz garbage).
 *
 * Emits raw frames: { t: audioCtx time, midi: float, rms } — the scorer decides
 * what to do with them. No rounding here: fractional MIDI carries the cents.
 */

export function createYin(options: {
  sampleRate: number;
  threshold: number;
  minFrequency: number;
  maxFrequency: number;
}): (buf: Float32Array) => number | null {
  const { sampleRate, threshold, minFrequency, maxFrequency } = options;
  return (buf: Float32Array): number | null => {
    const half = Math.floor(buf.length / 2);
    const tauMin = Math.max(2, Math.floor(sampleRate / maxFrequency));
    const tauMax = Math.min(half - 1, Math.ceil(sampleRate / minFrequency));
    if (tauMax <= tauMin) return null;

    // Step 2: difference function
    const d = new Float32Array(tauMax + 1);
    for (let tau = 1; tau <= tauMax; tau++) {
      let sum = 0;
      for (let i = 0; i < half; i++) {
        const delta = buf[i] - buf[i + tau];
        sum += delta * delta;
      }
      d[tau] = sum;
    }

    // Step 3: cumulative mean normalized difference
    const cmnd = new Float32Array(tauMax + 1);
    cmnd[0] = 1;
    let runningSum = 0;
    for (let tau = 1; tau <= tauMax; tau++) {
      runningSum += d[tau];
      cmnd[tau] = runningSum > 0 ? (d[tau] * tau) / runningSum : 1;
    }

    // Step 4: absolute threshold — first dip under threshold, walk to local min
    let tau = -1;
    for (let t = tauMin; t <= tauMax; t++) {
      if (cmnd[t] < threshold) {
        while (t + 1 <= tauMax && cmnd[t + 1] < cmnd[t]) t++;
        tau = t;
        break;
      }
    }
    if (tau === -1) return null;

    // Step 5: parabolic interpolation
    const x0 = tau > 1 ? tau - 1 : tau;
    const x2 = tau + 1 <= tauMax ? tau + 1 : tau;
    let betterTau = tau;
    if (x0 !== tau && x2 !== tau) {
      const s0 = cmnd[x0];
      const s1 = cmnd[tau];
      const s2 = cmnd[x2];
      const denom = 2 * (2 * s1 - s2 - s0);
      if (denom !== 0) betterTau = tau + (s2 - s0) / denom;
    }

    return sampleRate / betterTau;
  };
}

import { openMic, onDeviceChange, describeMicError, loadPreferredMic } from "./mic";

export interface RawPitchFrame {
  /** AudioContext.currentTime at capture */
  t: number;
  /** fractional MIDI, or null when unvoiced */
  midi: number | null;
  rms: number;
}

export type FrameCallback = (frame: RawPitchFrame) => void;

export class VoicePitchTracker {
  private audioContext: AudioContext;
  private analyser: AnalyserNode | null = null;
  private yinDetect: ((buf: Float32Array) => number | null) | null = null;
  private mediaStream: MediaStream | null = null;
  private sourceNode: AudioNode | null = null;
  private inputBuffer: Float32Array | null = null;
  private isRunning = false;
  private timerId: number | null = null;
  private onFrame: FrameCallback | null = null;
  /** which input this tracker opened, so a re-open can ask for it again */
  private activeDeviceId: string | null = null;
  private stopDeviceWatch: (() => void) | null = null;
  /** when the run of DIGITAL silence began (audio-clock seconds), or null */
  private deadSince: number | null = null;
  private deadReported = false;
  /** told when the stream is live but sending nothing — the Zoom symptom */
  onDeadStream: ((message: string) => void) | null = null;
  /** the label of the input actually in use, for the sound check */
  activeLabel: string | null = null;
  /** non-fatal note about the device chosen (a conferencing device, a fallback) */
  activeWarning: string | null = null;
  /** Analysis interval, ms. Interval-driven (NOT requestAnimationFrame) so detection
   *  keeps running when the browser throttles rAF (background tabs, low-power mode). */
  private readonly TICK_MS = 25;

  private readonly BUFFER_SIZE = 2048; // ~46ms @44.1k — enough for C2 with half-buffer search
  private readonly MIN_FREQUENCY = 60; // below B1
  private readonly MAX_FREQUENCY = 1400;
  private readonly YIN_THRESHOLD = 0.15;
  /**
   * Noise gate. Default suits a quiet room; calibration measures the actual
   * noise floor and raises this so room hum is never mistaken for singing.
   */
  private rmsGate = 0.004;

  /** Set the gate from a measured noise floor (calibration). */
  setNoiseFloor(floor: number): void {
    // sit the gate comfortably above the floor, but never below the default
    this.rmsGate = Math.max(0.004, floor * 2.5);
  }

  get noiseGate(): number {
    return this.rmsGate;
  }

  /** Provide a shared AudioContext (the same one the metronome/sampler run on). */
  constructor(ctx: AudioContext) {
    this.audioContext = ctx;
  }

  /**
   * Initialize from the user's microphone.
   *
   * `openMic` owns the retry ladder and the device choice; this owns keeping
   * the stream ALIVE afterwards — a track that ends or mutes (a call taking
   * the device) and a system input change both trigger a silent re-open, so
   * a singer mid-session never has to know any of it happened.
   */
  async initFromMic(deviceId?: string | null): Promise<void> {
    const wanted = deviceId ?? this.activeDeviceId ?? loadPreferredMic();
    const opened = await openMic(wanted);
    this.mediaStream = opened.stream;
    this.activeDeviceId = opened.deviceId;
    this.activeLabel = opened.label;
    this.activeWarning = opened.warning;

    // Safari hands back a silent graph if the context is still suspended when
    // the source is attached, and every browser needs it running to analyse.
    if (this.audioContext.state === 'suspended') {
      try { await this.audioContext.resume(); } catch { /* a gesture will do it */ }
    }

    this.attachSource(this.audioContext.createMediaStreamSource(this.mediaStream));
    this.watchStream();
  }

  /** Re-open the same input, quietly, keeping the analysis loop running. */
  private async reopen(): Promise<void> {
    const wasRunning = this.isRunning;
    try { this.sourceNode?.disconnect(); } catch { /* already gone */ }
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
    try {
      await this.initFromMic(this.activeDeviceId);
      this.deadSince = null;
      this.deadReported = false;
      this.isRunning = wasRunning;
    } catch (e) {
      this.onDeadStream?.(describeMicError(e));
    }
  }

  /**
   * A conferencing app taking the device shows up as the track ending or
   * muting; leaving a call shows up as a devicechange. Both mean the stream
   * we hold is no longer the one the singer is singing into.
   */
  private watchStream(): void {
    this.stopDeviceWatch?.();
    const track = this.mediaStream?.getAudioTracks()[0];
    if (track) {
      track.onended = () => { void this.reopen(); };
      track.onmute = () => { void this.reopen(); };
    }
    let debounce: number | null = null;
    this.stopDeviceWatch = onDeviceChange(() => {
      if (debounce !== null) window.clearTimeout(debounce);
      // a device switch fires a burst of events; settle before re-opening
      debounce = window.setTimeout(() => { void this.reopen(); }, 400);
    });
  }

  /** Initialize from an arbitrary node (used by the self-test / demo singer). */
  initFromNode(node: AudioNode): void {
    this.attachSource(node);
  }

  private attachSource(source: AudioNode): void {
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = this.BUFFER_SIZE;
    this.analyser.smoothingTimeConstant = 0; // raw time-domain data for YIN
    source.connect(this.analyser);
    this.sourceNode = source;
    this.inputBuffer = new Float32Array(this.analyser.fftSize);
    this.yinDetect = createYin({
      sampleRate: this.audioContext.sampleRate,
      threshold: this.YIN_THRESHOLD,
      minFrequency: this.MIN_FREQUENCY,
      maxFrequency: this.MAX_FREQUENCY,
    });
  }

  start(onFrame: FrameCallback): void {
    if (!this.yinDetect || !this.analyser || !this.inputBuffer) {
      throw new Error('Pitch tracker not initialized');
    }
    this.isRunning = true;
    this.onFrame = onFrame;
    this.timerId = window.setInterval(this.tick, this.TICK_MS);
    this.tick();
  }

  stop(): void {
    this.isRunning = false;
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private tick = (): void => {
    if (!this.isRunning || !this.analyser || !this.inputBuffer || !this.yinDetect) return;
    this.analyser.getFloatTimeDomainData(this.inputBuffer);
    const t = this.audioContext.currentTime;

    let sumSquares = 0;
    for (let i = 0; i < this.inputBuffer.length; i++) sumSquares += this.inputBuffer[i] * this.inputBuffer[i];
    const rms = Math.sqrt(sumSquares / this.inputBuffer.length);

    // A live-but-DEAD stream reads as exact zeroes, which no real microphone
    // in a real room ever produces. Two seconds of that is a held device, not
    // a quiet singer — say so instead of scoring silence as a missed note.
    if (rms === 0) {
      // Wall clock, NOT a frame count: a backgrounded tab throttles timers to
      // roughly one tick a second, and a frame threshold would take a minute
      // to trip there.
      if (this.deadSince === null) this.deadSince = t;
      if (t - this.deadSince > 2 && !this.deadReported) {
        this.deadReported = true;
        this.onDeadStream?.(
          'The microphone is open but sending pure silence — that is what happens when Zoom, Teams or Meet '
          + 'has hold of it, or when a conferencing device is selected as the input. Mute yourself in the call '
          + '(or pick a different input in the sound check) and it will come straight back.',
        );
      }
    } else {
      this.deadSince = null;
    }

    let midi: number | null = null;
    if (rms > this.rmsGate) {
      const freq = this.yinDetect(this.inputBuffer);
      if (freq !== null && freq >= this.MIN_FREQUENCY && freq <= this.MAX_FREQUENCY) {
        midi = 12 * Math.log2(freq / 440) + 69;
      }
    }
    this.onFrame?.({ t, midi, rms });
  };

  dispose(): void {
    this.stop();
    this.stopDeviceWatch?.();
    this.stopDeviceWatch = null;
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((tr) => tr.stop());
      this.mediaStream = null;
    }
    try { this.sourceNode?.disconnect(); } catch { /* already disconnected */ }
    this.analyser = null;
    this.yinDetect = null;
    this.inputBuffer = null;
  }
}
